import {
  AddAutomation,
  AutomationFormData,
  AutomationResponse,
  AutomationViewGroup,
  CreateAutomationPayload,
  DeleteAutomation,
  getAutomationPayload,
  UpdateAutomationPayload,
  UpdateAutomations,
} from '@tokeet-frontend/automations'
import * as lodash from 'lodash'
import { Store } from '@ngrx/store'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { Destroyable, isSomething } from '@tokeet-frontend/tv3-platform'

export abstract class AutomationDestroyableSaver extends Destroyable {
  isSilentCreation = false

  protected constructor(protected store: Store<any>, protected amplitudeService: AmplitudeService) {
    super()
  }

  getOperations(payload: CreateAutomationPayload, items: AutomationResponse[], groupId: string) {
    // Group items by rental|channel key
    const groupedItems = lodash.groupBy(items, (t) => `${t.settings?.rental || ''}|${t.settings?.channel || ''}`)

    // Keep only the first item for each group and collect duplicates for deletion
    const itemsGroups = lodash.mapValues(groupedItems, (group) => group[0])
    const duplicatesToDelete = lodash.flatten(
      lodash.map(groupedItems, (group) => group.slice(1)) // Get all items except the first one
    )

    const newKeys = lodash.uniq(
      lodash.flatten(lodash.map(payload.channels, (ch) => lodash.map(payload.rentals, (r) => `${r || ''}|${ch || ''}`)))
    )

    const existingKeys = lodash.keys(itemsGroups)

    const updates = lodash
      .intersection(existingKeys, newKeys)
      .map((key) => itemsGroups[key])
      .map((t): UpdateAutomationPayload => {
        return {
          ...lodash.omit(payload, ['channels', 'rentals']),
          pkey: t.pkey,
          channel: t.settings.channel,
          rental: t.settings.rental,
          group_id: groupId,
        }
      })

    // An item whose rental|channel is no longer selected is deleted, never re-pointed at
    // one of the new keys. Re-pointing rewrote settings.rental on a live automation, so
    // whenever the create of the remaining keys was lost the automation silently ended up
    // scoped to the first rental instead of the selected ones (TV4-6238).
    let deletes = lodash.difference(existingKeys, newKeys).map((key) => itemsGroups[key])
    if (isSomething(duplicatesToDelete)) {
      deletes = [...deletes, ...duplicatesToDelete]
    }

    const addKeys = lodash.difference(newKeys, existingKeys).map((rc) => lodash.split(rc, '|'))

    // The create endpoint multiplies rentals by channels. A single payload would
    // therefore re-create pairs that already exist whenever the missing keys do not form
    // a full grid, and the duplicate sends the message twice. One batch per channel keeps
    // the product equal to the missing pairs (TV4-6238).
    const adds = lodash.toPairs(lodash.groupBy(addKeys, ([, channel]) => channel || '')).map(
      ([channel, keys]): CreateAutomationPayload => ({
        ...payload,
        group_id: groupId,
        rentals: lodash.uniq(keys.map(([rental]) => rental)),
        channels: [channel],
      })
    )

    const data: { adds: CreateAutomationPayload[]; deletes: string[]; updates: UpdateAutomationPayload[] } = {
      adds,
      deletes: deletes.map((t) => t.pkey),
      updates,
    }

    return data
  }

  protected save(form: AutomationFormData, automation?: AutomationViewGroup, silentUpdate = false) {
    const payload = getAutomationPayload({ ...form, attributes: { ...automation?.attributes, ...form.attributes } })

    if (!!automation) {
      const groupId = automation.group_id || lodash.head(automation.items).pkey
      const { adds, updates, deletes } = this.getOperations(payload, automation.items, groupId)

      if (isSomething(adds)) {
        // The removals ride on the last batch, because AddAutomation drops them only once
        // its create succeeded. A failed create then leaves the automation as it was, and
        // never as a single narrowed rental (TV4-6238).
        adds.forEach((batch, index) => {
          const ids = index === adds.length - 1 ? deletes : []
          this.store.dispatch(AddAutomation({ ids, payload: batch, silent: true }))
        })
      } else if (isSomething(deletes)) {
        this.store.dispatch(DeleteAutomation({ ids: deletes, silent: true }))
      }

      if (isSomething(updates)) {
        this.store.dispatch(UpdateAutomations({ items: updates, silent: silentUpdate }))
      }
      this.amplitudeService.logEvent(`update-automation-${payload.event}`)
    } else {
      this.store.dispatch(AddAutomation({ payload, silent: this.isSilentCreation }))
      this.amplitudeService.logEvent(`add-automation-${payload.event}`)
    }
  }
}
